/**
 * TMS Event Log Page — Audit trail for jobs, trips, status changes
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Activity, Clock, Loader2, Plus } from 'lucide-react';
import { api } from '@/lib/api';

interface EventLog {
  id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  actor: string;
  details: string;
  created_at: string;
}

function normalizeEvent(raw: Record<string, unknown>): EventLog {
  let changes: Record<string, unknown> = {};
  try { changes = typeof raw.changes === 'string' ? JSON.parse(raw.changes as string) : (raw.changes as Record<string, unknown>) ?? {}; } catch { /* ignore */ }
  const actor = String(raw.actor ?? raw.actor_id ?? raw.actor_type ?? '');
  const details = String(raw.details ?? raw.description ?? changes?.message ?? changes?.entity_id ?? JSON.stringify(changes).replace(/[{}"]| /g, '').slice(0, 80) ?? '');
  return {
    id: String(raw.id ?? ''),
    entity_type: String(raw.entity_type ?? ''),
    entity_id: String(raw.entity_id ?? ''),
    action: String(raw.action ?? ''),
    actor,
    details,
    created_at: String(raw.created_at ?? ''),
  };
}

export default function TmsEventLogPage() {
  const [events, setEvents] = useState<EventLog[]>([]);
  const [search, setSearch] = useState('');
  const [jobSearch, setJobSearch] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); });
  const [dateTo, setDateTo] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; });
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedError, setSeedError] = useState<string | null>(null);
  const autoSeededRef = useRef(false);

  const fetchEvents = useCallback(async (): Promise<number> => {
    setLoading(true);
    try {
      const res = await api.get<{ items?: Record<string, unknown>[] } | Record<string, unknown>[]>('/activity-log?limit=100');
      const raw = Array.isArray(res) ? res : (res.items ?? []);
      const mapped = raw.map(normalizeEvent);
      setEvents(mapped);
      return mapped.length;
    } catch {
      return 0;
    } finally {
      setLoading(false);
    }
  }, []);

  const seedDemoEvents = useCallback(async () => {
    setSeeding(true);
    setSeedError(null);
    try {
      await api.post('/activity-log/seed-demo', {});
      await fetchEvents();
    } catch (err) {
      setSeedError(err instanceof Error ? err.message : 'Seed failed — check API connection');
    } finally {
      setSeeding(false);
    }
  }, [fetchEvents]);

  useEffect(() => {
    fetchEvents().then((count) => {
      if (count === 0 && !autoSeededRef.current) {
        autoSeededRef.current = true;
        seedDemoEvents();
      }
    });
  }, [fetchEvents, seedDemoEvents]);

  const filtered = events.filter((e) => {
    const q = search.toLowerCase();
    const jobQ = jobSearch.toLowerCase();
    const driverQ = driverSearch.toLowerCase();
    const createdDate = e.created_at ? String(e.created_at).slice(0, 10) : '';
    if (dateFrom && createdDate && createdDate < dateFrom) return false;
    if (dateTo && createdDate && createdDate > dateTo) return false;
    if (q && ![e.entity_type, e.entity_id, e.action, e.actor, e.details].some((v) => v?.toLowerCase().includes(q))) return false;
    if (jobQ && ![e.entity_id, e.details].some((v) => v?.toLowerCase().includes(jobQ))) return false;
    if (driverQ && ![e.actor, e.details].some((v) => v?.toLowerCase().includes(driverQ))) return false;
    return true;
  });

  const showFallback = filtered.length === 0 && events.length > 0;
  const displayEvents = showFallback
    ? [...events].sort((a, b) => b.created_at.localeCompare(a.created_at)).slice(0, 20)
    : filtered;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search events..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Input placeholder="Job no / entity id" value={jobSearch} onChange={(e) => setJobSearch(e.target.value)} className="h-9 w-[180px]" />
        <Input placeholder="Driver / actor" value={driverSearch} onChange={(e) => setDriverSearch(e.target.value)} className="h-9 w-[180px]" />
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-[150px]" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-[150px]" />
        <Badge variant="secondary">{filtered.length} events</Badge>
        {!loading && (
          <Button size="sm" variant="outline" className="h-9" onClick={seedDemoEvents} disabled={seeding}>
            {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
            {events.length === 0 ? 'Add Demo Events' : 'Reseed'}
          </Button>
        )}
      </div>
      {seedError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          ⚠ Seed error: {seedError}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : displayEvents.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          No events found. Try changing the date range or filters.
        </CardContent></Card>
      ) : (
        <div className="space-y-1">
          {showFallback && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 mb-2">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              No events on {dateFrom} — showing {displayEvents.length} most recent events.
            </div>
          )}
          {displayEvents.map((ev) => (
            <Card key={ev.id} className="hover:shadow-sm transition-shadow">
              <CardContent className="py-2 px-4 flex items-center gap-3">
                <Activity className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{ev.action}</span>
                    <Badge variant="outline" className="text-[10px]">{ev.entity_type}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">{ev.actor} · {ev.details}</div>
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-1 shrink-0">
                  <Clock className="h-3 w-3" />
                  {ev.created_at ? new Date(ev.created_at).toLocaleString() : '—'}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
